import React, { useEffect, useState } from "react";
import {
  Badge,
  Button,
  Card,
  Col,
  DatePicker,
  Descriptions,
  Modal,
  Popover,
  Row,
  Table,
  Tabs,
  Tag,
  Tooltip,
} from "antd";
import {
  CheckCircleOutlined,
  CloseCircleOutlined,
  WarningOutlined,
  CalendarOutlined,
  UnorderedListOutlined,
} from "@ant-design/icons";
import dayjs from "dayjs";
import { useLocation, useNavigate } from "react-router-dom";
import { post } from "../../../services/services";
import { ApiURL } from "../../../services/apiConstants";
import ViolationBadge from "../../../components/ViolationBadge";

/* ── constants ───────────────────────────────────────────────── */
const ENTRY_COLORS = {
  work: "#52c41a",
  rest: "#1890ff",
  break: "#fa8c16",
  meal: "#722ed1",
  overtime: "#f5222d",
};

const ENTRY_TAG_COLOR = {
  work: "blue", rest: "green", break: "orange", meal: "purple", overtime: "red",
};

const STATUS_COLOR = { draft: "default", closed: "success" };
const WEEK_DAYS = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];

/* ── helpers ─────────────────────────────────────────────────── */
const timeToFraction = (t = "00:00") => {
  const [h, m] = t.split(":").map(Number);
  return (h * 60 + m) / 1440;
};


const buildTimelineSegments = (day) => {
  const segments = [];

  day.working_hours?.forEach((range) => {
    const [start, end] = range.split("-");

    segments.push({
      start_time: start,
      end_time: end,
      entry_type: "work",
    });
  });

  day.rest_hours?.forEach((range) => {
    const [start, end] = range.split("-");

    segments.push({
      start_time: start,
      end_time: end,
      entry_type: "rest",
    });
  });

  return segments.sort((a, b) =>
    a.start_time.localeCompare(b.start_time)
  );
};

const buildMiniBarSegments = (day) => {
  const segments = [];

  day.working_hours?.forEach((range) => {
    const [start, end] = range.split("-");

    segments.push({
      start_time: start,
      end_time: end,
      entry_type: "work",
    });
  });

  day.rest_hours?.forEach((range) => {
    const [start, end] = range.split("-");

    segments.push({
      start_time: start,
      end_time: end,
      entry_type: "rest",
    });
  });

  return segments.sort((a, b) =>
    a.start_time.localeCompare(b.start_time)
  );
};

/* ── sub-components ──────────────────────────────────────────── */

const timeToMinutes = (time = "00:00") => {
  if (time === "24:00") return 1440;

  const [h, m] = time.split(":").map(Number);
  return h * 60 + m;
};

/** 24-hour timeline bar */
const TimelineBar = ({ entries = [] }) => {
  if (!entries.length)
    return (
      <div style={{ textAlign: "center", color: "#bbb", padding: "20px 0", fontSize: 13 }}>
        No entries recorded for this day.
      </div>
    );

  return (
    <div>
      {/* hour tick marks */}
      <div style={{ position: "relative", height: 16, marginBottom: 2 }}>
        {[0, 2, 4, 6, 8, 10, 12, 14, 16, 18, 20, 22, 24].map((h) => (
          <span
            key={h}
            style={{
              position: "absolute",
              left: `${(h / 24) * 100}%`,
              fontSize: 10,
              color: "#aaa",
              transform: "translateX(-50%)",
              userSelect: "none",
            }}
          >
            {String(h).padStart(2, "0")}:00
          </span>
        ))}
      </div>

      {/* bar */}
      <div
        style={{
          position: "relative",
          height: 36,
          background: "#f0f0f0",
          borderRadius: 8,
          overflow: "hidden",
          border: "1px solid #e0e0e0",
        }}
      >
        {entries.map((entry, i) => {
          const start =
            timeToMinutes(entry.start_time);

          const end =
            timeToMinutes(
              entry.end_time === "23:59"
                ? "24:00"
                : entry.end_time
            );

          const left =
            (start / 1440) * 100;

          let width =
            ((end - start) / 1440) * 100;

          if (left + width > 100) {
            width = 100 - left;
          }
          return (
            <Tooltip
              key={i}
              title={
                <div>
                  <div style={{ fontWeight: 600, textTransform: "capitalize" }}>{entry.entry_type}</div>
                  <div>{entry.start_time} → {entry.end_time}</div>
                  <div>
                    {((end - start) / 60).toFixed(1)} hrs
                  </div>
                  {entry.description && <div style={{ color: "#ccc", fontSize: 11 }}>{entry.description}</div>}
                </div>
              }
            >
              <div
                style={{
                  position: "absolute",
                  top: 0,
                  left: `${left}%`,
                  width: `${width}%`,
                  height: "100%",
                  background: ENTRY_COLORS[entry.entry_type] || "#ccc",
                  opacity: 0.9,
                  cursor: "pointer",
                  borderRight: "1px solid rgba(255,255,255,0.3)",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  overflow: "hidden",
                }}
              >
                {width > 6 && (
                  <span style={{ color: "#fff", fontSize: 10, fontWeight: 600, whiteSpace: "nowrap" }}>
                    {((end - start) / 60).toFixed(1)}h
                  </span>
                )}
              </div>
            </Tooltip>
          );
        })}
      </div>

      {/* legend */}
      <div style={{ display: "flex", gap: 12, flexWrap: "wrap", marginTop: 10 }}>
        {Object.entries(ENTRY_COLORS).map(([type, color]) => (
          <div key={type} style={{ display: "flex", alignItems: "center", gap: 4, fontSize: 11, color: "#555" }}>
            <div style={{ width: 12, height: 12, borderRadius: 3, background: color }} />
            <span style={{ textTransform: "capitalize" }}>{type}</span>
          </div>
        ))}
      </div>

      {/* entry detail chips */}
      <div style={{ display: "flex", gap: 8, flexWrap: "wrap", marginTop: 12 }}>
        {entries.map((e, i) => (
          <div
            key={i}
            style={{
              display: "flex",
              alignItems: "center",
              gap: 6,
              background: `${ENTRY_COLORS[e.entry_type]}15`,
              border: `1px solid ${ENTRY_COLORS[e.entry_type]}44`,
              borderRadius: 20,
              padding: "3px 10px",
              fontSize: 12,
            }}
          >
            <div style={{ width: 8, height: 8, borderRadius: "50%", background: ENTRY_COLORS[e.entry_type] }} />
            <span style={{ textTransform: "capitalize", fontWeight: 600, color: ENTRY_COLORS[e.entry_type] }}>
              {e.entry_type}
            </span>
            <span style={{ color: "#555" }}>{e.start_time}–{e.end_time}</span>
            <span style={{ color: "#999" }}>
              {(
                (
                  timeToMinutes(
                    e.end_time === "23:59"
                      ? "24:00"
                      : e.end_time
                  ) -
                  timeToMinutes(e.start_time)
                ) / 60
              ).toFixed(1)}
              h
            </span>
          </div>
        ))}
      </div>
    </div>
  );
};


const MiniBar = ({ day }) => {
  const entries =
    buildMiniBarSegments(day);

  return (
    <div
      style={{
        position: "relative",
        height: 5,
        borderRadius: 2,
        overflow: "hidden",
        background: "#f0f0f0",
      }}
    >
      {entries.map((entry, i) => {
        const start =
          timeToMinutes(entry.start_time);

        const end =
          timeToMinutes(
            entry.end_time === "23:59"
              ? "24:00"
              : entry.end_time
          );

        return (
          <div
            key={i}
            style={{
              position: "absolute",
              left: `${(start / 1440) * 100}%`,
              width: `${((end - start) / 1440) * 100}%`,
              height: "100%",
              background:
                ENTRY_COLORS[
                entry.entry_type
                ],
            }}
          />
        );
      })}
    </div>
  );
};

/** Calendar grid */
const CalendarGrid = ({ record, selectedDay, onSelectDay, onAddEntry }) => {
  if (!record) return null;

  const firstDay = dayjs(`${record.year}-${record.month}-01`).day(); // 0=Sun
  const offset = firstDay === 0 ? 6 : firstDay - 1;               // Mon-first
  const daysInMonth = record.days_in_month;
  const dayMap = Object.fromEntries((record.days || []).map((d) => [d.day, d]));

  const cells = [
    ...Array(offset).fill(null),
    ...Array.from({ length: daysInMonth }, (_, i) => i + 1),
  ];

  return (
    <div>
      {/* week headers */}
      <div style={{ display: "grid", gridTemplateColumns: "repeat(7, 1fr)", gap: 4, marginBottom: 4 }}>
        {WEEK_DAYS.map((d) => (
          <div
            key={d}
            style={{
              textAlign: "center",
              fontSize: 11,
              fontWeight: 700,
              color: d === "Sat" || d === "Sun" ? "#1890ff" : "#888",
              padding: "4px 0",
            }}
          >
            {d}
          </div>
        ))}
      </div>

      {/* day cells */}
      <div style={{ display: "grid", gridTemplateColumns: "repeat(7, 1fr)", gap: 4 }}>
        {cells.map((dayNum, idx) => {
          if (!dayNum) return <div key={`e-${idx}`} />;
          const d = dayMap[dayNum];
          const isSelected = selectedDay?.day === dayNum;
          const hasData = d?.has_data;
          const compliant = d?.compliant;
          const vCount = d?.violations?.length || 0;

          return (
            <Tooltip
              key={dayNum}
              title={
                hasData
                  ? `Rest: ${d.rest_hours}h | Work: ${d.work_hours}h | Violations: ${vCount}`
                  : "No data"
              }
            >
              <div
                onClick={() => {
                  if (!d) return;
                  if (d.has_data) onSelectDay(d);
                  else onAddEntry(d);
                }}
                style={{
                  borderRadius: 8,
                  padding: "6px 4px 5px",
                  cursor: d ? "pointer" : "default",
                  border: isSelected
                    ? "2px solid #1890ff"
                    : hasData
                      ? `1.5px solid ${compliant ? "#b7eb8f" : "#ffccc7"}`
                      : "1.5px solid #f0f0f0",
                  background: isSelected
                    ? "#e6f7ff"
                    : hasData
                      ? compliant
                        ? "#f6ffed"
                        : "#fff2f0"
                      : "#fafafa",
                  transition: "all 0.15s",
                  minHeight: 56,
                  position: "relative",
                }}
              >
                <div
                  style={{
                    fontSize: 13,
                    fontWeight: isSelected ? 800 : 500,
                    color: isSelected ? "#1890ff" : hasData ? (compliant ? "#389e0d" : "#cf1322") : "#bbb",
                    lineHeight: 1,
                    marginBottom: 2,
                  }}
                >
                  {dayNum}
                </div>

                {hasData && (
                  <>
                    <div style={{ fontSize: 9, color: "#888", lineHeight: 1.3 }}>
                      R:{d.rest_hours}h W:{d.work_hours}h
                    </div>
                    <MiniBar day={d} />
                    {vCount > 0 && (
                      <Badge
                        count={vCount}
                        size="small"
                        style={{
                          position: "absolute",
                          top: 3,
                          right: 3,
                          fontSize: 9,
                          minWidth: 14,
                          height: 14,
                          lineHeight: "14px",
                          padding: "0 3px",
                        }}
                      />
                    )}
                  </>
                )}
              </div>
            </Tooltip>
          );
        })}
      </div>
    </div>
  );
};

/* ── table columns ───────────────────────────────────────────── */
const entryColumns = [
  { title: "Start", dataIndex: "start_time", key: "start_time", align: "center" },
  { title: "End", dataIndex: "end_time", key: "end_time", align: "center" },
  {
    title: "Duration (hrs)", dataIndex: "duration", key: "duration", align: "center",
    render: (v) => Number(v).toFixed(1),
  },
  {
    title: "Type", dataIndex: "entry_type", key: "entry_type", align: "center",
    render: (t) => (
      <Tag color={ENTRY_TAG_COLOR[t] || "default"} style={{ textTransform: "capitalize" }}>{t}</Tag>
    ),
  },
  {
    title: "Description", dataIndex: "description", key: "description", align: "center",
    render: (t) => t || "—",
  },
];

const dayColumns = [
  { title: "Day", dataIndex: "day", key: "day", align: "center", width: 60 },
  { title: "Date", dataIndex: "date", key: "date", align: "center", width: 110 },
  {
    title: "Rest (hrs)", dataIndex: "rest_total_hours", key: "rest_total_hours", align: "center", width: 100,
    render: (v) => (v != null ? Number(v).toFixed(1) : "—"),
  },
  {
    title: "Work (hrs)", dataIndex: "work_hours", key: "work_hours", align: "center", width: 100,
    render: (v) => (v != null ? Number(v).toFixed(1) : "—"),
  },
  {
    title: "7-Day Rest", dataIndex: "rolling_7d_rest", key: "rolling_7d_rest", align: "center", width: 110,
    render: (v) => (v != null ? Number(v).toFixed(1) : "—"),
  },
  {
    title: "7-Day Work", dataIndex: "rolling_7d_work", key: "rolling_7d_work", align: "center", width: 110,
    render: (v) => (v != null ? Number(v).toFixed(1) : "—"),
  },
  {
    title: "Compliant", dataIndex: "compliant", key: "compliant", align: "center", width: 100,
    render: (v) =>
      v ? (
        <CheckCircleOutlined style={{ color: "#52c41a", fontSize: 18 }} />
      ) : (
        <CloseCircleOutlined style={{ color: "#ff4d4f", fontSize: 18 }} />
      ),
  },
  {
    title: "Violations", key: "violations", align: "center", width: 120,
    render: (_, row) => {
      if (!row.violations?.length) return "—";
      const content = (
        <div style={{ maxWidth: 340 }}>
          {row.violations.map((v, i) => (
            <ViolationBadge key={i} rule={v.rule} detail={v.detail} />
          ))}
        </div>
      );
      return (
        <Popover content={content} title="Violations" trigger="click">
          <Badge count={row.violations.length} style={{ cursor: "pointer" }}>
            <Tooltip title="Click to view">
              <WarningOutlined style={{ color: "#ff4d4f", fontSize: 18, cursor: "pointer" }} />
            </Tooltip>
          </Badge>
        </Popover>
      );
    },
  },
  {
    title: "Entries", key: "entries", align: "center", width: 80,
    render: (_, row) =>
      row.entries?.length ? <Tag color="processing">{row.entries.length}</Tag> : <Tag>0</Tag>,
  },
];

/* ── main component ──────────────────────────────────────────── */
const RecordsRestHours = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [page_loader, set_page_loader] = useState(false);
  const [record, setRecord] = useState(null);
  const [selectedMonth, setSelectedMonth] = useState(
    location.state?.year && location.state?.month
      ? dayjs(`${location.state.year}-${String(location.state.month).padStart(2, "0")}-01`)
      : dayjs()
  );
  const [selectedDay, setSelectedDay] = useState(null);
  const selectedStaffId = location.state?.staff_id || null;
  const selectedShipId = location.state?.ship_id || null;

  const handleAddEntry = (day) => {
    Modal.confirm({
      title: `No entry for ${day.date}`,
      content: `Would you like to add a log entry for ${dayjs(day.date).format("DD MMM YYYY")}?`,
      okText: "Yes, Add Entry",
      cancelText: "No",
      onOk: () => navigate("/log-hours", {
        state: {
          source_panel: "staff",
          date: day.date,
          staff_id: selectedStaffId,
          ship_id: selectedShipId,
          returnTo: location.state?.returnTo,
          filters: location.state?.filters,
        },
      }),
    });
  };

  useEffect(() => {
    FETCH_RECORD(selectedMonth);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /* auto-select day when record loads */
  useEffect(() => {
    if (!record?.days?.length) return;
    const today = dayjs();
    const isThisMonth = record.year === today.year() && record.month === today.month() + 1;
    const autoDay = isThisMonth
      ? record.days.find((d) => d.day === today.date()) || record.days.find((d) => d.has_data)
      : record.days.find((d) => d.has_data);
    setSelectedDay(autoDay || record.days[0]);
  }, [record]);

  const FETCH_RECORD = (month) => {
    set_page_loader(true);
    const FORM_DATA = new FormData();
    FORM_DATA.append("year", month.format("YYYY"));
    FORM_DATA.append("month", month.format("M"));
    if (selectedStaffId) FORM_DATA.append("staff_id", selectedStaffId);
    if (selectedShipId) FORM_DATA.append("ship_id", selectedShipId);
    post(ApiURL.view_rest_hours_record_st, FORM_DATA).then((res) => {
      setRecord(res?.status === true ? res.data || null : null);
      set_page_loader(false);
    });
  };

  return (
    <>
      {/* Month Picker */}
      <div className="row" style={{ marginBottom: 10, marginTop: 10 }}>
        <div className="col-4" style={{ marginLeft: 15 }}>
          <label className="block-label">Select Month</label>
          <DatePicker
            picker="month"
            value={selectedMonth}
            format="MMMM YYYY"
            style={{ width: "100%" }}
            disabledDate={(c) => c && c > dayjs().endOf("month")}
            onChange={(val) => { if (val) setSelectedMonth(val); }}
          />
        </div>
        <div className="col-2" style={{ alignSelf: "flex-end" }}>
          <Button
            type="primary"
            className="save_btn"
            style={{ marginBottom: 13 }}
            loading={page_loader}
            onClick={() => FETCH_RECORD(selectedMonth)}
          >
            Search
          </Button>
        </div>
      </div>

      {/* Summary Card */}
      {record && (
        <div className="card p-3 mb-3">
          <Descriptions bordered size="small" column={4}>
            <Descriptions.Item label="Staff Name" span={2}>{record.staff_name}</Descriptions.Item>
            <Descriptions.Item label="Period">
              {dayjs(`${record.year}-${record.month}-01`).format("MMMM YYYY")}
            </Descriptions.Item>
            <Descriptions.Item label="Status">
              <Tag color={STATUS_COLOR[record.record_status] || "default"} style={{ textTransform: "capitalize" }}>
                {record.record_status}
              </Tag>
            </Descriptions.Item>
            <Descriptions.Item label="Total Rest"><strong>{record.total_rest_hours} hrs</strong></Descriptions.Item>
            <Descriptions.Item label="Total Work"><strong>{record.total_work_hours} hrs</strong></Descriptions.Item>
            <Descriptions.Item label="Violations">
              <Tag color={record.total_violations > 0 ? "error" : "success"}>{record.total_violations}</Tag>
            </Descriptions.Item>
            <Descriptions.Item label="Compliance">
              {record.is_compliant ? (
                <Tag color="success" icon={<CheckCircleOutlined />}>Compliant</Tag>
              ) : (
                <Tag color="error" icon={<CloseCircleOutlined />}>Non-Compliant</Tag>
              )}
            </Descriptions.Item>
            <Descriptions.Item label="Seafarer Signed">{record.seafarer_signed_at || "Not signed"}</Descriptions.Item>
            <Descriptions.Item label="Master Signed">{record.master_signed_at || "Not signed"}</Descriptions.Item>
            <Descriptions.Item label="Closed At" span={2}>{record.closed_at || "—"}</Descriptions.Item>
          </Descriptions>
        </div>
      )}

      {/* Monthly violation banner */}
      {record?.total_violations > 0 && (
        <div
          style={{
            background: "#FFEBEE",
            border: "1px solid #ffccc7",
            borderRadius: 8,
            padding: "10px 16px",
            marginBottom: 16,
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <div>
            <span style={{ color: "#D32F2F", fontWeight: 700 }}>
              {record.total_violations} violation(s) this month
            </span>
            <span style={{ color: "#888", fontSize: 12, marginLeft: 10 }}>
              Select a day in the calendar to see details
            </span>
          </div>
        </div>
      )}

      {/* Tabs */}
      <Tabs
        defaultActiveKey="calendar"
        items={[
          {
            key: "calendar",
            label: <span><CalendarOutlined /> Calendar View</span>,
            children: (
              <Row gutter={[16, 16]}>
                {/* Calendar */}
                <Col xs={24} lg={14}>
                  <Card
                    title={
                      <span style={{ fontSize: 14 }}>
                        {record
                          ? dayjs(`${record.year}-${record.month}-01`).format("MMMM YYYY")
                          : "Monthly Calendar"}
                      </span>
                    }
                    style={{ borderRadius: 12, border: "none", boxShadow: "0 2px 12px rgba(0,0,0,0.08)" }}
                    styles={{ body: { padding: 16 } }}
                    extra={
                      <div style={{ display: "flex", gap: 10, fontSize: 11 }}>
                        <span><span style={{ color: "#52c41a", marginRight: 3 }}>●</span>Compliant</span>
                        <span><span style={{ color: "#f5222d", marginRight: 3 }}>●</span>Non-Compliant</span>
                        <span><span style={{ color: "#d9d9d9", marginRight: 3 }}>●</span>No data</span>
                      </div>
                    }
                  >
                    <CalendarGrid
                      record={record}
                      selectedDay={selectedDay}
                      onSelectDay={setSelectedDay}
                      onAddEntry={handleAddEntry}
                    />
                  </Card>
                </Col>

                {/* Timeline panel */}
                <Col xs={24} lg={10}>
                  <Card
                    title={
                      selectedDay ? (
                        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                          <span style={{ fontSize: 14 }}>
                            Day {selectedDay.day} — {selectedDay.date}
                          </span>
                          {selectedDay.has_data && (
                            selectedDay.compliant ? (
                              <Tag color="success" style={{ fontSize: 11 }}>Compliant</Tag>
                            ) : (
                              <Tag color="error" style={{ fontSize: 11 }}>Non-Compliant</Tag>
                            )
                          )}
                        </div>
                      ) : (
                        "Select a day"
                      )
                    }
                    style={{ borderRadius: 12, border: "none", boxShadow: "0 2px 12px rgba(0,0,0,0.08)" }}
                    styles={{ body: { padding: 16 } }}
                  >
                    {selectedDay ? (
                      <>
                        {/* violations */}
                        {selectedDay.violations?.length > 0 && (
                          <div style={{ marginBottom: 12 }}>
                            {selectedDay.violations.map((v, i) => (
                              <ViolationBadge key={i} rule={v.rule} detail={v.detail} />
                            ))}
                          </div>
                        )}

                        {/* stats row */}
                        {selectedDay.has_data && (
                          <div
                            style={{
                              display: "flex",
                              gap: 12,
                              marginBottom: 14,
                              padding: "8px 0",
                              borderBottom: "1px solid #f0f0f0",
                            }}
                          >
                            {[
                              { label: "Rest", value: selectedDay.rest_total_hours, color: "#1890ff" },
                              { label: "Work", value: selectedDay.work_hours, color: "#52c41a" },
                              { label: "7d Rest", value: selectedDay.rolling_7d_rest, color: "#1890ff" },
                              { label: "7d Work", value: selectedDay.rolling_7d_work, color: "#52c41a" },
                            ].map(({ label, value, color }) => (
                              <div key={label} style={{ textAlign: "center", flex: 1 }}>
                                <div style={{ fontSize: 16, fontWeight: 700, color }}>{Number(value).toFixed(0)}h</div>
                                <div style={{ fontSize: 10, color: "#888" }}>{label}</div>
                              </div>
                            ))}
                          </div>
                        )}

                        {/* 24h timeline */}
                        <TimelineBar entries={buildTimelineSegments(selectedDay)} />
                      </>
                    ) : (
                      <div style={{ textAlign: "center", color: "#bbb", padding: "40px 0" }}>
                        Click a day on the calendar to view details.
                      </div>
                    )}
                  </Card>
                </Col>
              </Row>
            ),
          },
          {
            key: "table",
            label: <span><UnorderedListOutlined /> Table View</span>,
            children: (
              <Table
                bordered
                columns={dayColumns}
                dataSource={record?.days || []}
                rowKey="day"
                loading={page_loader}
                className="custom-table"
                pagination={false}
                rowClassName={(row) =>
                  !row.has_data ? "" : row.compliant ? "" : "ant-table-row-danger"
                }
                // expandable={{
                //   expandedRowRender: (row) =>
                //     row.entries?.length ? (
                //       <Table
                //         bordered
                //         size="small"
                //         columns={entryColumns}
                //         dataSource={row.entries}
                //         rowKey="id"
                //         pagination={false}
                //         style={{ margin: "8px 0" }}
                //       />
                //     ) : (
                //       <p style={{ margin: 0, color: "#999" }}>No entries for this day.</p>
                //     ),
                //   rowExpandable: (row) => row.has_data,
                // }}
                scroll={{ x: 800 }}
              />
            ),
          },
        ]}
      />

      <style>{`
        .ant-table-row-danger td { background-color: #fff2f0 !important; }
      `}</style>
    </>
  );
};

export default RecordsRestHours;
